import { AxiosInstance } from 'axios';
import { plainToInstance } from 'class-transformer';
import { MessageDTO } from '../commons/dtos/message.dto';
import { IMessage, IMessageRequest } from '../commons/interfaces/chat.interface';
import {
  IChatbotAPIResponse,
  IChatbotAPIResponsePagination
} from '../commons/interfaces/chatbotAPI.interface';
import { ITicket, ITicketPatchRequest } from '../commons/interfaces/ticket.interface';
import { Unit } from '../commons/interfaces/unit.interface';
import Axios from './axios.service';

export default class ChatbotAPI {
  private request: AxiosInstance;

  constructor() {
    this.request = Axios.getInstance();
  }

  listUnits = async () => {
    return this.request.get<IChatbotAPIResponse<Unit[]>>('units');
  };

  listUnitTickets = async (
    unitId: string,
    page: number,
    status: string,
    search: string,
    managerId: string
  ) => {
    const manager = status === 'WAITING' ? '' : managerId;

    return this.request.get<IChatbotAPIResponsePagination<ITicket[]>>(`/units/${unitId}/tickets`, {
      params: { pageSize: 10, page, status, search, manager }
    });
  };

  updateUnits = async (unitId: string, data: string) => {
    return this.request.put(`/units/${unitId}`, { data });
  };

  listTickets = async (
    unitId: string,
    page: number,
    status: string,
    startDate: string,
    endDate: string,
    search = '',
    managerId = ''
  ) => {
    const manager = status === 'WAITING' ? '' : managerId;

    return this.request.get<IChatbotAPIResponsePagination<ITicket[]>>('/tickets', {
      params: { pageSize: 10, page, status, search, unitId, managerId: manager, startDate, endDate }
    });
  };

  getTicket = async (ticketId: string) => {
    return this.request.get<IChatbotAPIResponse<ITicket>>(`/tickets/${ticketId}`);
  };

  listTicketMessages = async (ticketId: string, page: number) => {
    const response = await this.request.get<IChatbotAPIResponsePagination<IMessage[]>>(
      `/tickets/${ticketId}/messages`,
      { params: { pageSize: 20, page } }
    );

    const data = plainToInstance(MessageDTO, response.data.data);

    return { data, pagination: response.data.pagination };
  };

  uploadFile = async (formData: FormData) => {
    const response = await this.request.post(`/tickets/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return { url: response.data };
  };

  createTicketMessage = async (ticketId: string, body: IMessageRequest) => {
    const response = await this.request.post<IChatbotAPIResponse<IMessage>>(
      `/tickets/${ticketId}/messages`,
      body
    );

    const data = plainToInstance(MessageDTO, response.data.data);

    return { data };
  };

  modifyTicket = async (ticketId: string, body: ITicketPatchRequest) => {
    const path = `/tickets/${ticketId}`;
    const response = await this.request.patch<IChatbotAPIResponse<ITicket>>(path, body);

    return response.data;
  };

  exportTickets = async (unitId: string, status: string, startDate: string, endDate: string) => {
    try {
      const response = await this.request.post(
        '/tickets/export',
        { unitId, status, startDate, endDate },
        { responseType: 'blob' }
      );

      const outputFilename = 'Reporte_de_tickets.xlsx';
      const url = URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', outputFilename);
      link.click();
      link.remove();

      return response;
    } catch (err: any) {
      throw new Error(err);
    }
  };
}
